import type { PropsWithChildren } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type StepFrameProps = PropsWithChildren<{
  title: string;
  description: string;
  canBack?: boolean;
  onBack?: () => void;
  nextLabel?: string;
  hideNext?: boolean;
  nextDisabled?: boolean;
  isSubmitting?: boolean;
  isReadOnly?: boolean;
  mobileSummary?: string;
}>;

export function StepFrame({
  title,
  description,
  children,
  canBack,
  onBack,
  nextLabel = "Continue",
  hideNext,
  nextDisabled,
  isSubmitting,
  isReadOnly,
  mobileSummary,
}: StepFrameProps) {
  const hasNavigation = canBack || !hideNext;

  return (
    <Card className="rounded-lg">
      <CardHeader className="rounded-t-lg border-b-4 border-primary bg-accent px-4 py-4 text-accent-foreground sm:px-6 sm:py-6">
        <CardTitle className="text-xl sm:text-2xl">{title}</CardTitle>
        <CardDescription className="text-sm leading-6 text-white/78 sm:text-base">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pb-28 pt-5 sm:pb-6 sm:pt-7">
        {children}
        {!isReadOnly && hasNavigation ? (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-accent/10 bg-white/95 px-4 py-3 shadow-[0_-12px_30px_hsl(229_61%_34%_/_0.16)] backdrop-blur sm:static sm:mt-8 sm:flex sm:flex-row sm:justify-between sm:border-t-0 sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none">
            {mobileSummary ? <p className="mb-2 text-center text-xs font-extrabold text-accent sm:hidden">{mobileSummary}</p> : null}
            <div className="grid grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)] gap-3 sm:flex sm:w-full sm:justify-between">
              {canBack ? (
                <Button type="button" variant="outline" onClick={onBack} className="w-full sm:w-auto">
                  Back
                </Button>
              ) : (
                <span className="hidden sm:block" />
              )}
              {!hideNext ? (
                <Button type="submit" disabled={isSubmitting || nextDisabled} className="w-full sm:w-auto">
                  {isSubmitting ? "Working..." : nextLabel}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
